import { useEffect, useMemo, useState } from 'react';
import type { ScheduleOwnerDto, ScheduleRunSummaryDto } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert } from '@/components/ui/alert';
import { hub } from '@/lib/hub-client';
import {
  buildScheduleRunsQuery,
  mapScheduleApiError,
} from './schedule-ui-model';
import { RUN_HISTORY_COLUMNS, RunHistoryRow } from './run-history-row';

interface RunHistoryProps {
  open: boolean;
  schedule: ScheduleOwnerDto | null;
  onClose: () => void;
}

export function RunHistory({ open, schedule, onClose }: RunHistoryProps) {
  const [runs, setRuns] = useState<ScheduleRunSummaryDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !schedule) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setRuns([]);

    hub.schedulesRuns({ params: { id: schedule.id }, query: buildScheduleRunsQuery() })
      .then((response) => {
        if (!cancelled) setRuns(response.items);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(mapScheduleApiError(caught));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, schedule]);

  const newestFirstRuns = useMemo(
    () => [...runs].sort((left, right) => Date.parse(right.scheduledFor) - Date.parse(left.scheduledFor)),
    [runs],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Latest scheduled runs"
      description={schedule ? `Latest 20 redacted outcomes for ${schedule.scheduleExpr}. Raw metadata and tool payloads are not shown.` : undefined}
      className="max-w-6xl"
      footer={<Button variant="outline" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-3">
        {error ? <Alert>{error}</Alert> : null}

        {isLoading ? (
          <p role="status" aria-live="polite" className="text-sm text-muted-foreground">Loading latest 20 runs…</p>
        ) : null}

        <Table>
          <TableHeader>
            <TableRow>
              {RUN_HISTORY_COLUMNS.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {newestFirstRuns.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={RUN_HISTORY_COLUMNS.length} className="text-center text-muted-foreground">
                  {error ? 'Run history could not be loaded.' : 'No recorded runs yet.'}
                </TableCell>
              </TableRow>
            ) : (
              newestFirstRuns.map((run) => <RunHistoryRow key={run.id} run={run} />)
            )}
          </TableBody>
        </Table>

        <p className="text-xs text-muted-foreground">
          Showing {newestFirstRuns.length} run{newestFirstRuns.length === 1 ? '' : 's'} from the owner-scoped run-history endpoint, capped at 20.
        </p>
      </div>
    </Dialog>
  );
}
